/* eslint-disable no-underscore-dangle,no-param-reassign */
import React, { FC, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { IconSearch } from '@lmn/lastminute-react-icons/lastminute-icons-linear';
import { mediaQuery } from '../utils-css/media-query';
import { SearchResult } from '../../../types/search';

const SearchBoxContainer = styled.div<{ $show: boolean }>`
  transform: translate(0, 0);
  position: absolute;
  top: 8px;
  right: 15px;
  opacity: ${(props): string => `${props.$show ? 1 : 0}`};
  transition: opacity 0.3s linear;

  ${mediaQuery.minWidth.lg} {
    position: static;
  }
`;

interface StartSearchProps {
  $searching: boolean;
}

const SearchAltContainer = styled.span<StartSearchProps>`
  position: absolute;
  top: 50%;
  right: -7px;
  transform: translate(-50%, -50%);
  color: ${({ theme }): string => theme.commons.colors.primary};
  transition: 0.2s;

  ${(props) =>
    props.$searching
      ? css`
          opacity: 0;
          z-index: -1;
        `
      : null}
`;

const SearchBoxInput = styled.input<StartSearchProps>`
  width: 35px;
  height: 35px;
  background: none;
  border-radius: 50px;
  box-sizing: border-box;
  font-size: ${(props): string => props.theme.commons.font.sizes.medium};
  border: 2px solid ${({ theme }): string => theme.commons.colors.primary};
  outline: none;
  transition: 0.2s;
  color: transparent;

  ${(props) =>
    props.$searching
      ? css`
          width: 200px;
          border-radius: 4px;
          color: ${({ theme }): string => theme.commons.colors.primary};
        `
      : null}
`;

interface SearchBoxProps {
  $openMenu: boolean;
  onClick: () => void;
  onSearch: (searchValue: string) => void;
}

const labelId = 'search';

const SearchBox: FC<StartSearchProps & SearchBoxProps> = ({ $openMenu, $searching, onClick, onSearch }) => (
  <SearchBoxContainer $show={!$openMenu}>
    <SearchBoxInput
      aria-label="search"
      id={labelId}
      $searching={$searching}
      placeholder={$searching ? 'Search' : ''}
      onChange={(event): void => {
        onSearch(event.target.value);
      }}
      disabled={!$searching}
    />
    <SearchAltContainer $searching={$searching} onClick={onClick}>
      <IconSearch size={25} />
    </SearchAltContainer>
  </SearchBoxContainer>
);

interface SearchProps {
  $openMenu: boolean;
  searching: boolean;
  onClick: () => void;
  onHits: (hits: SearchResult[]) => void;
}

const hasMinimumCharsToSearch = (query: string): boolean => query.length >= 3;

const isASearchInProgressUsing = (searching: boolean, query: string, hits: SearchResult[]): boolean =>
  searching && query !== null && query !== undefined && hasMinimumCharsToSearch(query) && hits.length > 0;

export const Search: FC<SearchProps> = ({ $openMenu, searching, onClick, onHits }) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (query) {
      const { index, store } = window.__LUNR__.en;
      const enhancedQuery = query
        .split(' ')
        .map((term) => `${term}*`)
        .join(' ');
      const searchResults = index.search(enhancedQuery);
      const hits = searchResults.map(({ ref }) => store[ref]);
      if (isASearchInProgressUsing(searching, query, hits)) {
        onHits(hits);
      } else {
        onHits([]);
      }
    }
  }, [query]);

  return (
    <SearchBox
      $openMenu={$openMenu}
      $searching={searching}
      onClick={onClick}
      onSearch={(searchValue): void => setQuery(searchValue)}
    />
  );
};
